<template lang="html">
  <div>
    <CarTypes
      v-if="types?.length"
      :types="types"
      @toggleCarType="onToggleCarType"
    />

    <slot />

    <div
      v-html="description"
      class="prose prose-sm dark:prose-white prose-neutral mt-2 block lg:hidden"
    ></div>

    <h3 class="font-bold block mb-2 mt-3">Finansiering</h3>

    <fieldset
      class="tabs-bar w-full gap-1 md:gap-4 sm:w-max mb-6 md:mb-6"
      v-if="fundings.length > 1"
    >
      <template v-for="{ name, value } in fundings" :key="value">
        <input
          v-model="funding"
          type="radio"
          :value="value"
          name="funding"
          :id="`funding-${value}`"
        />
        <label
          :class="[
            'sm:w-auto',
            { 'w-1/2': fundings.length == 2 },
            { 'w-1/2': fundings.length == 3 },
          ]"
          :for="`funding-${value}`"
          >{{ name }}</label
        >
      </template>
    </fieldset>

    <div class="mt-6">
      <financial-calculator
        v-if="financial.price && financing.includes('buy')"
        key="buy"
        v-show="funding === 'buy'"
        :shouldUpdate="funding === 'buy'"
        :price="financialData.price"
        :prev-prices="isClubman && forClubman ? prevPrices : null"
        :prevOptionsSum="prevOptionsSum"
        :disclaimer="disclaimer"
        :capital="financialData.capital_percentage"
        :optionsSum="optionsSum"
        :accessoriesSum="accessoriesSum"
        :funding="funding"
        :nominal="interestRate"
        ref="buy"
        :currentColor="currentColor"
      >
      </financial-calculator>

      <LeasingCalculator
        v-if="
          financialData.leasing?.kilometer_fees.length > 0 &&
          financialData.leasing?.upfront_costs.length > 0 &&
          financialData.leasing?.starting_price_base > 0 &&
          financing.includes('leasing')
        "
        v-show="funding === 'leasing'"
        :key="`leasing-${financialData.leasing?.starting_price_base}`"
        :shouldUpdate="funding === 'leasing'"
        :financial="financialData.leasing"
        :optionsSum="optionsSum"
        ref="leasing"
        :currentColor="currentColor"
        :prev-prices="isClubman && forClubman ? prevPrices : null"
        :prevOptionsSum="prevOptionsSum"
        :nominal="interestRate"
      />
      <SubscriptionCalculator
        v-if="
          financialData.subscription?.kilometer_fees.length > 0 &&
          financialData.subscription?.duration_fees.length > 0 &&
          financing.includes('subscription')
        "
        key="subscription"
        v-show="funding === 'subscription'"
        :shouldUpdate="funding === 'subscription'"
        :financial="financialData?.subscription"
        :optionsSum="optionsSum"
        :accessoriesSum="accessoriesSum"
        ref="subscription"
        :currentColor="currentColor"
        :prev-prices="isClubman && forClubman ? prevPrices : null"
        :prevOptionsSum="prevOptionsSum"
      />
    </div>

    <div class="mt-8 md:mt-16" :key="`options-${typeName}-${funding}`">
      <div
        class="mt-4 md:mt-8"
        v-for="(optionGroup, i) in (isClubman ? clubmanTypes : types)[typeIdx]
          .options_groups"
        :key="i"
      >
        <div
          v-if="
            optionGroup?.options?.length > 0 &&
            optionGroup?.options[0].prices[this.funding]
          "
        >
          <h2 class="text-grey-900 dark:text-gold-50 font-bold text-xl">
            {{ optionGroup.title }}
          </h2>
          <div class="flex flex-col gap-1 md:gap-4 mt-7">
            <LabbeledCheckbox
              v-for="(option, key) in optionGroup['options']"
              @toggleOption="(option) => onToggleOption(option, 'option')"
              :key="key"
              :title="option.option"
              :prices="option.prices"
              :description="option.description"
              :typeCharge="typeCharge"
              :funding="funding"
              :activeOptions="activeOptions"
              :prev-price="getPrevPriceForOption(i, key)"
              :singleChoice="
                optionGroup.single_choice ? optionGroup.title : false
              "
            />
          </div>
        </div>
      </div>
    </div>

    <div>
      <div
        class="content mt-6 col-span-2 text-sm"
        v-if="disclaimer"
        v-html="disclaimer"
      ></div>
    </div>
  </div>
</template>

<script>
import LabbeledCheckbox from '../partials/LabbeledCheckbox.vue';
import LeasingCalculator from './LeasingCalculator.vue';
import SubscriptionCalculator from './SubscriptionCalculator.vue';
import CarTypes from './CarTypes.vue';

export default {
  name: 'ComplexCalculator',
  components: {
    LabbeledCheckbox,
    LeasingCalculator,
    SubscriptionCalculator,
    CarTypes,
  },
  props: {
    financial: Object,
    clubmanFinancial: Object,
    types: Array | Boolean,
    clubmanTypes: Array | Boolean,
    description: String,
    isClubman: Boolean,
    forClubman: Boolean,
    financing: {
      type: Array,
      default: () => ['buy', 'leasing', 'subscription'],
    },
  },
  data(vm) {
    return {
      typeIdx: 0,
      colorsGroups: [],
      currentColor: 0,
      financialData: this.isClubman ? this.clubmanFinancial : this.financial,
      disclaimer: this.isClubman
        ? this.clubmanFinancial.disclaimer
        : this.financial.disclaimer,
      funding: parent.location.hash.replace('#', '') || 'buy',
      fundings: [],
      activeOptions: [],
      optionsSum: 0,
      prevOptionsSum: 0,
      activeAccessories: [],
      accessoriesSum: 0,
      inputOptions: { value: null },
      typeCharge: [],
      typeName: 'base',
      prevPrices: this.financial,
      interestRate: 0,
    };
  },
  mounted() {
    this.assignDomNodes();
    this.initColorPicker();
    this.initFundings();
    this.calcInterestRate(this.funding);
  },

  methods: {
    calcInterestRate(funding) {
      this.interestRate =
        funding === 'leasing' && this.financialData.leasing.interest_rate
          ? +this.financialData.leasing.interest_rate
          : +this.financialData.interest_rate || +this.financial.interest_rate;
    },
    getPrevPriceForOption(i, key) {
      const getPrice = (forClubman) =>
        this.types[this.typeIdx].options_groups[i].options[key].prices[
          forClubman ? 'clubman_' + this.funding : this.funding
        ];

      return getPrice() !== getPrice(true) && this.isClubman
        ? getPrice()
        : null;
    },
    assignDomNodes() {
      this.colorsGroups = document.querySelectorAll('.car-color-picker');
      this.colorsImages = document.querySelectorAll('.car-color-images');
      this.inputOptions = document.querySelector('.input-car-options');
    },
    initColorPicker() {
      this.colorsGroups.forEach((colorGroup, i) => {
        const isntActive = i !== this.typeIdx;
        colorGroup.classList.toggle('hidden', isntActive);
        this.colorsImages[i].classList.toggle('hidden', isntActive);

        Array.from(colorGroup.children).forEach((btn, i) => {
          btn.addEventListener('click', () => this.onColorChange(i));
        });
      });

      this.onColorChange();
    },
    initFundings() {
      let availableFunding = [];

      if (this.financing.includes('buy')) {
        availableFunding.push({
          name: 'Kjøp',
          value: 'buy',
        });
      }

      if (
        this.financialData?.leasing?.kilometer_fees.length > 0 &&
        this.financialData?.leasing?.upfront_costs.length > 0 &&
        this.financialData?.leasing?.starting_price_base > 0 &&
        this.financing.includes('leasing')
      ) {
        availableFunding.push({
          name: 'Leasing',
          value: 'leasing',
        });
      }

      if (
        this.financialData?.subscription?.kilometer_fees.length > 0 &&
        this.financialData?.subscription?.duration_fees.length > 0 &&
        this.financing.includes('subscription')
      ) {
        availableFunding.push({
          name: 'Abonnement',
          value: 'subscription',
        });
      }

      this.fundings = availableFunding;
      this.funding = availableFunding.some((f) => f.value === this.funding)
        ? this.funding
        : availableFunding[0].value;
      this.updateDisclaimer();
    },
    onColorChange(colorIdx = 0) {
      const types = this.isClubman ? this.clubmanTypes : this.types;

      const colorPrices = types[this.typeIdx]?.colors[colorIdx]?.prices;
      const prevColorPrices =
        this.types[this.typeIdx]?.colors[colorIdx]?.prices;

      if (colorPrices) {
        const { price_base, leasing, subscription } = this.financialData;

        if (colorPrices.base) {
          this.financialData.price = +price_base + +colorPrices.base;

          this.prevPrices.price =
            +this.financial.price_base + +prevColorPrices.base;
        }

        if (colorPrices.leasing) {
          leasing.starting_price =
            +leasing.starting_price_base + +colorPrices.leasing;

          this.prevPrices.leasing.starting_price =
            +this.financial.leasing.starting_price_base +
            +prevColorPrices.leasing;
        }

        if (colorPrices.subscription) {
          subscription.starting_price =
            +subscription.starting_price_base + +colorPrices.subscription;

          this.prevPrices.subscription.starting_price =
            +this.financial.subscription.starting_price_base +
            +prevColorPrices.subscription;
        }
      }

      this.currentColor = +`${this.typeIdx + 1}${colorIdx + 1}`;
    },
    onToggleCarType({ index }) {
      const types = this.isClubman ? this.clubmanTypes : this.types;
      const prevTypes = this.types;

      if (index || index === 0) {
        this.typeCharge = types[index];
        this.optionsSum = 0;
        this.activeOptions = [];
        this.typeName = types[index]?.type;
        const { base, leasing, subscription } = types[index].prices;
        const financial = { ...this.financialData };

        if (base) {
          financial.price = parseInt(base);
          financial.price_base = parseInt(base);

          this.prevPrices.price = +prevTypes[index].prices.base;
          this.prevPrices.price_base = +prevTypes[index].prices.base;
        }

        if (leasing) {
          financial.leasing.starting_price = parseInt(leasing);
          financial.leasing.starting_price_base = parseInt(leasing);

          this.prevPrices.leasing.starting_price =
            +prevTypes[index].prices.leasing;
          this.prevPrices.leasing.starting_price_base =
            +prevTypes[index].prices.leasing;
        } else {
          financial.leasing.starting_price_base = 0;
        }

        if (subscription) {
          financial.subscription.starting_price = parseInt(subscription);
          financial.subscription.starting_price_base = parseInt(subscription);

          this.prevPrices.subscription.starting_price =
            +prevTypes[index].prices.subscription;
          this.prevPrices.subscription.starting_price_base =
            +prevTypes[index].prices.subscription;
        } else {
          financial.subscription.starting_price = 0;
        }

        this.colorsGroups.forEach((colorGroup, i) => {
          const isntActive = i !== index;
          const colorImage = this.colorsImages[i];
          colorGroup.classList.toggle('hidden', isntActive);
          colorImage.classList.toggle('hidden', isntActive);

          if (!isntActive) {
            colorGroup.children[0].setAttribute('aria-selected', true);
            colorImage.children[0]?.classList.remove('opacity-0', 'delay-200');
          }
        });

        this.financialData = financial;

        this.typeIdx = index;
        this.onColorChange();
        this.initFundings();

        return;
      }

      this.financialData = JSON.parse(
        JSON.stringify(this.isClubman ? this.clubmanFinancial : this.financial)
      );

      // this.prevPrices = JSON.parse(JSON.stringify(this.financial));
    },
    updateDisclaimer() {
      const types = this.isClubman ? this.clubmanTypes : this.types;
      const index = this.typeIdx;
      if (types[index].disclaimer) {
        this.disclaimer = types[index].disclaimer;
        return;
      }

      switch (this.funding) {
        case 'buy':
          this.disclaimer = this.financialData.disclaimer;
          break;
        case 'leasing':
          this.disclaimer = this.financialData.leasing.disclaimer;
          break;
        case 'subscription':
          this.disclaimer = this.financialData.subscription.disclaimer;
          break;
      }
    },
    onToggleOption(option) {
      if (option.singleChoice) {
        this.activeOptions = this.activeOptions.filter((item) => {
          return item.singleChoice !== option.singleChoice;
        });
        if (option.checked) {
          this.activeOptions = [...this.activeOptions, option];
        }
      } else {
        this.activeOptions = option.checked
          ? [...this.activeOptions, option]
          : this.activeOptions.filter((item) => item.title !== option.title);
      }

      this.calculateOptionsSum(this.activeOptions);
      this.fillContactInput();
    },
    calculateOptionsSum(options) {
      const funding = this.isClubman ? 'clubman_' + this.funding : this.funding;
      this.optionsSum = options.reduce((a, v) => a + +v[funding], 0);
      this.prevOptionsSum = options.reduce((a, v) => a + +v[this.funding], 0);

      this.$refs[this.funding].change();
    },
    fillContactInput() {
      if (!this.inputOptions) return;
      const price = this.isClubman ? 'clubman_buy' : 'buy';

      this.inputOptions.value = this.activeOptions.reduce(
        (a, v) => a + `${v.title} - ${v[price] ?? v.buy} NOK,  `,
        ''
      );
    },
  },
  watch: {
    funding(funding) {
      if (this.$refs[funding]) {
        this.updateDisclaimer();
        this.calculateOptionsSum(this.activeOptions, 'optionsSum');
        this.$refs[funding].change();
        parent.location.hash = funding;
      }
      this.calcInterestRate(funding);
    },
  },
};
</script>
